/**
 * Recensement des couleurs de la NOUVELLE interface (variables CSS `--nu-*` de
 * src/styles/ui-new.scss) exposées à l'édition côté BO (onglet Couleurs, nouveau
 * bloc « Interface & 3D »). Chaque entrée = une variable surchargée au runtime
 * (cf. utils/cssColors.applyUiColorOverrides). Les valeurs par défaut sont les
 * valeurs canoniques de ui-new.scss → « retour aux couleurs par défaut ».
 *
 * Forward-compat : un site sans surcharge garde les défauts (la surcharge ne pose
 * QUE les clés fournies). L'ancien bloc de 4 couleurs (--color-*) reste géré à part
 * (« Couleurs (legacy) ») pour ne pas casser les sites existants.
 */
export interface UiColorToken {
    /** Clé stockée (map site.uiColors) = suffixe de la variable CSS. */
    key: string;
    /** Variable CSS surchargée. */
    cssVar: string;
    /** Valeur par défaut (ui-new.scss). */
    default: string;
    /** Groupe d'affichage BO. */
    group: 'brand' | 'text' | 'surface' | 'status';
    /** Clé i18n du libellé. */
    labelKey: string;
}

export const UI_COLOR_TOKENS: UiColorToken[] = [
    // Marque
    { key: 'magenta', cssVar: '--nu-magenta', default: '#ec1e78', group: 'brand', labelKey: 'UiColorMagenta' },
    {
        key: 'magenta-wash',
        cssVar: '--nu-magenta-wash',
        default: '#fde7f1',
        group: 'brand',
        labelKey: 'UiColorMagentaWash'
    },
    { key: 'cyan', cssVar: '--nu-cyan', default: '#0aa5c2', group: 'brand', labelKey: 'UiColorCyan' },
    { key: 'cyan-wash', cssVar: '--nu-cyan-wash', default: '#e0f4f8', group: 'brand', labelKey: 'UiColorCyanWash' },
    { key: 'yellow', cssVar: '--nu-yellow', default: '#f5c518', group: 'brand', labelKey: 'UiColorYellow' },
    // Texte
    { key: 'ink', cssVar: '--nu-ink', default: '#171a1f', group: 'text', labelKey: 'UiColorInk' },
    { key: 'ink-soft', cssVar: '--nu-ink-soft', default: '#4a4f58', group: 'text', labelKey: 'UiColorInkSoft' },
    { key: 'ink-faint', cssVar: '--nu-ink-faint', default: '#878d98', group: 'text', labelKey: 'UiColorInkFaint' },
    // Surfaces
    { key: 'paper', cssVar: '--nu-paper', default: '#eef0f4', group: 'surface', labelKey: 'UiColorPaper' },
    { key: 'surface', cssVar: '--nu-surface', default: '#ffffff', group: 'surface', labelKey: 'UiColorSurface' },
    { key: 'surface-2', cssVar: '--nu-surface-2', default: '#f6f7f9', group: 'surface', labelKey: 'UiColorSurface2' },
    { key: 'line', cssVar: '--nu-line', default: '#e4e7ec', group: 'surface', labelKey: 'UiColorLine' },
    {
        key: 'line-strong',
        cssVar: '--nu-line-strong',
        default: '#cfd4dc',
        group: 'surface',
        labelKey: 'UiColorLineStrong'
    },
    // États
    { key: 'good', cssVar: '--nu-good', default: '#17936b', group: 'status', labelKey: 'UiColorGood' },
    { key: 'warn', cssVar: '--nu-warn', default: '#d9860b', group: 'status', labelKey: 'UiColorWarn' }
];

/** Map { key: defaultHex } — base d'un état BO / reset complet. */
export function defaultUiColors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const t of UI_COLOR_TOKENS) {
        out[t.key] = t.default;
    }
    return out;
}
